import { AdminHeader } from "@/components/AdminHeader";
import { useEffect, useRef, useState } from "react";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Upload, Trash2, Film, ImageIcon, CheckCircle2 } from "lucide-react";

export default function GalleryTab() {
  const [items, setItems] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = () => api.get("/admin/gallery").then((r) => setItems(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);

  const upload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        fd.append("title", f.name);
        await api.post("/admin/gallery", fd);
      }
      toast.success(files.length > 1 ? `${files.length} items uploaded — approve them to publish` : "Uploaded — approve it to publish");
      load();
    } catch (e2) {
      toast.error(errMsg(e2));
    }
    setUploading(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  const approve = async (item) => {
    try {
      await api.patch(`/admin/gallery/${item.id}/approve`);
      toast.success("Approved — now live on the website");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const remove = async (item) => {
    if (!window.confirm(`Delete "${item.title}" from the gallery?`)) return;
    try {
      await api.delete(`/admin/gallery/${item.id}`);
      toast.success("Deleted from gallery");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div data-testid="admin-gallery-tab">
      <AdminHeader
        testid="admin-header-gallery"
        eyebrow="Website"
        title="Media Gallery"
        description="Uploads stay hidden until you approve them — approved items show in the homepage Island Gallery."
        actions={<>
          <input ref={fileRef} data-testid="gallery-upload-input" type="file" accept="image/*,video/*" multiple onChange={upload} className="hidden" />
          <Button data-testid="gallery-upload-button" disabled={uploading} onClick={() => fileRef.current?.click()} className="rounded-full bg-[#C71E2E] text-white hover:bg-[#A01825] font-ui">
            <Upload size={14} className="mr-2" /> {uploading ? "Uploading…" : "Add photos / videos"}
          </Button>
        </>}
      />
      {items.length === 0 ? (
        <p data-testid="gallery-empty" className="text-sm text-muted-foreground py-14 text-center border border-dashed border-[#E5DED5] rounded-xl">
          No media yet — upload photos or videos for the website.
        </p>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {items.map((item) => (
            <div key={item.id} data-testid={`gallery-item-${item.id}`} className="relative rounded-xl overflow-hidden border border-[#E5DED5] bg-card">
              {item.kind === "video" ? (
                <video src={item.url} muted loop playsInline className="w-full h-36 object-cover bg-black" onMouseEnter={(e) => e.target.play()} onMouseLeave={(e) => e.target.pause()} />
              ) : (
                <img src={item.url} alt={item.title} className="w-full h-36 object-cover" />
              )}
              {!item.approved && (
                <span data-testid={`gallery-pending-${item.id}`} className="absolute top-2 left-2 text-[10px] font-ui uppercase tracking-[0.12em] bg-amber-500/90 text-white rounded-full px-2.5 py-1">
                  Pending approval
                </span>
              )}
              {item.approved && (
                <span data-testid={`gallery-live-${item.id}`} className="absolute top-2 left-2 text-[10px] font-ui uppercase tracking-[0.12em] bg-emerald-500/90 text-white rounded-full px-2.5 py-1">
                  Live on site
                </span>
              )}
              <div className="p-2.5 flex items-center justify-between gap-2">
                <span className="text-xs font-ui text-muted-foreground truncate flex items-center gap-1.5">
                  {item.kind === "video" ? <Film size={11} className="shrink-0" /> : <ImageIcon size={11} className="shrink-0" />}
                  {item.title}
                </span>
                <div className="flex items-center gap-2 shrink-0">
                  {!item.approved && (
                    <button data-testid={`gallery-approve-${item.id}`} onClick={() => approve(item)} className="text-emerald-600 hover:text-emerald-700 transition-colors" aria-label="Approve">
                      <CheckCircle2 size={16} />
                    </button>
                  )}
                  <button data-testid={`gallery-delete-${item.id}`} onClick={() => remove(item)} className="text-red-400 hover:text-red-600 transition-colors" aria-label="Delete">
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
